import React, {isValidElement, useState} from 'react';

import cs from 'classnames';
import {Dropdown, DropdownItem, DropdownMenu, DropdownToggle} from 'reactstrap';

export interface ActionMenuItemType {
  text: string | React.ReactElement;
  icon: string;
  disabled?: boolean;
  key: string | number;
  callback: () => unknown;
  condition?: () => boolean;
  className?: string;
}

export type ActionsMenuItemsType = Array<ActionMenuItemType>;

export type ActionButtonBuilderType = (buttonProps: {
  children: React.ReactElement;
  className: string;
  disabled: boolean;
}) => React.ReactElement<'button'>;

interface Props {
  actions: ActionsMenuItemsType;
  children?: React.ReactElement | ActionButtonBuilderType;
  stopClickPropagation?: boolean;
  fixedDropdown?: boolean;
}

/* generic component to show a button which opens a dropdown menu with several avilable actions */
export const ActionsButton = React.forwardRef<HTMLDivElement, Props>(
  (
    {
      actions,
      children = defaultButtonBuilder,
      stopClickPropagation = false,
      fixedDropdown = false,
    }: Props,
    buttonRef
  ): JSX.Element | null => {
    const [isPending, setPending] = useState(false);
    const [isOpen, setOpen] = useState(false);

    // I swear I tried to cancel the promise on unmount but it didn't work
    // so we track the mounted status and avoid updating state after it's unmounted
    const isMounted = React.useRef(true);

    React.useEffect(
      () => () => {
        isMounted.current = false;
      },
      []
    );

    if (actions.length < 1) {
      return null;
    }

    const getIcon = (icon: string) =>
      isPending ? 'fas fa-circle-notch fa-spin' : icon;

    function captureCallbackPromise(cb: () => unknown, ev: React.MouseEvent) {
      if (stopClickPropagation) {
        ev.stopPropagation();
      }
      setPending(true);

      function restorePending<V>(e: V): V {
        if (isMounted.current) {
          setPending(false);
        }
        return e;
      }

      return Promise.resolve(cb()).then(restorePending, restorePending);
    }

    const button = isValidElement(children)
      ? children
      : children({
          className: 'btn btn-link py-0 px-1',
          disabled: isPending,
          children: <i className={getIcon('fa fa-fw fa-ellipsis-v')} />,
        });

    const dropdownItems = actions.filter(
      (action) => action.condition?.() ?? true
    );
    if (dropdownItems.length === 0) {
      return null;
    }

    return (
      <div ref={buttonRef} className="js-edit-dropdown">
        {isOpen ? (
          <Dropdown
            isOpen={isOpen}
            toggle={(event) => {
              if (stopClickPropagation) {
                event.stopPropagation();
              }
              setOpen(!isOpen);
            }}
          >
            <DropdownToggle
              color=""
              className=""
              disabled={isPending}
              tag="div"
            >
              {button}
            </DropdownToggle>
            <DropdownMenu right positionFixed={fixedDropdown}>
              {dropdownItems.map(
                ({text, icon, disabled, callback, key, className}) => (
                  <DropdownItem
                    className={cs('btn-sm btn-link', className)}
                    onClick={(ev) => captureCallbackPromise(callback, ev)}
                    disabled={disabled}
                    key={key}
                  >
                    <i className={`fa-fw ${icon}`} /> {text}
                  </DropdownItem>
                )
              )}
            </DropdownMenu>
          </Dropdown>
        ) : (
          /* performance: don't render the menu if it`s not opened */
          <div
            onClick={
              isPending
                ? undefined
                : (event) => {
                    if (stopClickPropagation) {
                      event.stopPropagation();
                    }
                    setOpen(true);
                  }
            }
          >
            {button}
          </div>
        )}
      </div>
    );
  }
);

export const defaultButtonBuilder: ActionButtonBuilderType = (buttonProps) => (
  <button {...buttonProps} />
);

export const SpacingDummyActionButton = (): JSX.Element => (
  <div className="px-1">
    <i className="fa fa-fw" />
  </div>
);
